import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.fileInput = this.element.querySelector('input[type="file"]')
    this.dropZone = this.element.querySelector("#drop-zone")
    this.progressWrapper = this.element.querySelector("#upload-progress")
    this.progressBar = this.element.querySelector("#progress-bar")
    this.status = this.element.querySelector("#upload-status")
    this.count = this.element.querySelector("#upload-count")
    this.dragIndicator = this.element.querySelector("#drag-indicator")

    this.maxFileSize = 50 * 1024 * 1024 // 50MB sanity limit — compression handles normal oversized files
    this.totalUploads = 0
    this.completedUploads = 0

    this.registerEventListeners()
    this.resetProgress()
  }

  disconnect() {
    this.unregisterEventListeners()
  }

  registerEventListeners() {
    if (!this.element) return

    this.boundInitialize = this.handleDirectUploadInitialize.bind(this)
    this.boundStart = this.handleDirectUploadStart.bind(this)
    this.boundProgress = this.handleDirectUploadProgress.bind(this)
    this.boundEnd = this.handleDirectUploadEnd.bind(this)
    this.boundError = this.handleDirectUploadError.bind(this)

    this.element.addEventListener("direct-upload:initialize", this.boundInitialize)
    this.element.addEventListener("direct-upload:start", this.boundStart)
    this.element.addEventListener("direct-upload:progress", this.boundProgress)
    this.element.addEventListener("direct-upload:end", this.boundEnd)
    this.element.addEventListener("direct-upload:error", this.boundError)

    if (this.dropZone) {
      this.boundDragEnter = this.handleDragEnter.bind(this)
      this.boundDragOver = this.handleDragOver.bind(this)
      this.boundDragLeave = this.handleDragLeave.bind(this)
      this.boundDrop = this.handleDrop.bind(this)

      this.dropZone.addEventListener("dragenter", this.boundDragEnter)
      this.dropZone.addEventListener("dragover", this.boundDragOver)
      this.dropZone.addEventListener("dragleave", this.boundDragLeave)
      this.dropZone.addEventListener("drop", this.boundDrop)
    }

    if (this.fileInput) {
      this.boundChange = this.updateSelectionSummary.bind(this)
      this.fileInput.addEventListener("change", this.boundChange)
    }
  }

  unregisterEventListeners() {
    if (!this.element) return

    this.element.removeEventListener("direct-upload:initialize", this.boundInitialize)
    this.element.removeEventListener("direct-upload:start", this.boundStart)
    this.element.removeEventListener("direct-upload:progress", this.boundProgress)
    this.element.removeEventListener("direct-upload:end", this.boundEnd)
    this.element.removeEventListener("direct-upload:error", this.boundError)

    if (this.dropZone) {
      this.dropZone.removeEventListener("dragenter", this.boundDragEnter)
      this.dropZone.removeEventListener("dragover", this.boundDragOver)
      this.dropZone.removeEventListener("dragleave", this.boundDragLeave)
      this.dropZone.removeEventListener("drop", this.boundDrop)
    }

    if (this.fileInput) {
      this.fileInput.removeEventListener("change", this.boundChange)
    }
  }

  handleDirectUploadInitialize() {
    this.totalUploads += 1
    this.updateUploadCount()
  }

  handleDirectUploadStart() {
    this.showProgress()
    this.status && (this.status.textContent = "Uploading photos to Cloudinary…")
  }

  handleDirectUploadProgress(event) {
    if (!this.progressBar) return
    const progress = event.detail.progress
    this.progressBar.style.width = `${progress}%`
  }

  handleDirectUploadEnd() {
    this.completedUploads += 1
    this.updateUploadCount()

    if (this.completedUploads >= this.totalUploads) {
      this.status && (this.status.textContent = "Upload complete!")
      setTimeout(() => this.resetProgress(), 1200)
    }
  }

  handleDirectUploadError(event) {
    if (this.status) {
      this.status.textContent = `Upload failed: ${event.detail.error}`
    }
    this.progressWrapper && this.progressWrapper.classList.remove("hidden")
    this.progressBar && (this.progressBar.style.width = "0%")
  }

  handleDragEnter(event) {
    event.preventDefault()
    this.highlightDropZone(true)
  }

  handleDragOver(event) {
    event.preventDefault()
    this.highlightDropZone(true)
  }

  handleDragLeave(event) {
    if (!this.dropZone || !this.element.contains(event.relatedTarget)) {
      this.highlightDropZone(false)
    }
  }

  handleDrop(event) {
    event.preventDefault()
    this.highlightDropZone(false)

    if (!this.fileInput) return

    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      this.fileInput.files = files
      this.fileInput.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  highlightDropZone(active) {
    if (!this.dropZone) return

    this.dropZone.classList.toggle("border-indigo-500", active)
    this.dropZone.classList.toggle("ring", active)
    this.dropZone.classList.toggle("ring-offset-2", active)
    this.dragIndicator && this.dragIndicator.classList.toggle("hidden", !active)
  }

  updateSelectionSummary() {
    if (!this.fileInput || !this.count) return

    const files = Array.from(this.fileInput.files)
    const validFiles = this.filterOversizedFiles(files)

    if (validFiles.length !== files.length) {
      this.replaceFileInputFiles(validFiles)
      this.showOversizedWarning(files, validFiles)
    }

    this.count.textContent = `${this.fileInput.files.length} selected`
  }

  updateUploadCount() {
    if (!this.count) return

    this.count.textContent = `${this.completedUploads} of ${this.totalUploads}`
  }

  showProgress() {
    this.progressWrapper && this.progressWrapper.classList.remove("hidden")
  }

  resetProgress() {
    this.totalUploads = 0
    this.completedUploads = 0
    this.updateUploadCount()
    this.progressBar && (this.progressBar.style.width = "0%")
    this.progressWrapper && this.progressWrapper.classList.add("hidden")
    this.status && (this.status.textContent = "Processing photos…")
  }

  filterOversizedFiles(files) {
    return files.filter(file => file.size <= this.maxFileSize)
  }

  replaceFileInputFiles(files) {
    if (!this.fileInput) return

    const dataTransfer = new DataTransfer()
    files.forEach(file => dataTransfer.items.add(file))
    this.fileInput.files = dataTransfer.files
  }

  showOversizedWarning(originalFiles, validFiles) {
    const oversized = originalFiles.filter(file => file.size > this.maxFileSize)
    if (oversized.length === 0) return

    const names = oversized.map(file => file.name).join(", ")
    alert(`The following files exceed the 10MB limit and were removed: ${names}`)
  }
}
